/**
 * GraphQL Booking Service
 * Provides booking operations using the .NET GraphQL API.
 * Maintains compatibility with existing frontend interfaces.
 *
 * Logging strategy:
 * - INFO: Booking created, confirmed, cancelled, status changed (business events)
 * - WARN: Failed booking operations
 * - ERROR: Unexpected exceptions
 * - DEBUG: Query operations, data fetches
 */

import logger from '../logger';
import { graphqlClient, extractData } from './client';
import {
  MY_BOOKINGS_QUERY,
  BOOKING_BY_ID_QUERY,
  BOOKINGS_BY_SERVICE_PROVIDER_QUERY,
  ALL_BOOKINGS_QUERY,
  CREATE_BOOKING_MUTATION,
  UPDATE_BOOKING_STATUS_MUTATION,
  CONFIRM_ANONYMOUS_BOOKING_MUTATION,
  CONFIRM_MY_BOOKING_MUTATION,
  CANCEL_BOOKING_MUTATION,
  CANCEL_MY_BOOKING_MUTATION,
  SUBMIT_CONTACT_MUTATION,
  SUBMIT_FEEDBACK_MUTATION,
} from './queries';
import type {
  GraphQLBooking,
  CreateBookingInput,
  MyBookingsResponse,
  BookingByIdResponse,
  BookingsByServiceProviderResponse,
  AllBookingsResponse,
  CreateBookingResponse,
  UpdateBookingStatusResponse,
  ConfirmAnonymousBookingResponse,
  ConfirmMyBookingResponse,
  CancelBookingResponse,
  CancelMyBookingResponse,
  BookingStatus,
  ContactInput,
  FeedbackInput,
  SubmitContactResponse,
  SubmitFeedbackResponse,
} from './types';

// ============================================
// Type Mappings for Frontend Compatibility
// ============================================

/**
 * Frontend BookingRecord interface
 * Maps GraphQL response to existing frontend format
 */
export interface BookingRecord {
  id?: string;
  $id?: string;
  $createdAt?: string;
  $updatedAt?: string;
  pickup_location: string;
  dropoff_location: string;
  pickup_date: string;
  pickup_time: string;
  pickup_type: 'asap' | 'scheduled';
  passenger_name: string;
  company_name?: string;
  country_code: string;
  passenger_mobile: string;
  passenger_email?: string;
  payment_method: string;
  communication_prefs: {
    voice?: boolean;
    sms?: boolean;
    whatsapp?: boolean;
    email?: boolean;
  };
  passengers: number;
  luggage: number;
  selected_ride: string;
  estimated_duration?: number;
  status: 'pending' | 'pending_confirmation' | 'confirmed' | 'cancelled' | 'completed';
  is_verified?: boolean;
  user_id?: string;
  booking_number?: string;
  status_reason?: string;
  serviceprovider_id?: string;
  service_provider_name?: string;
  booking_details?: string;
  is_anonymous_booking?: boolean;
}

/**
 * Convert GraphQL booking to frontend format
 */
function toBookingRecord(gqlBooking: GraphQLBooking): BookingRecord {
  return {
    id: gqlBooking.id,
    $id: gqlBooking.id,
    $createdAt: gqlBooking.createdAt,
    $updatedAt: gqlBooking.updatedAt,
    pickup_location: gqlBooking.pickupLocation,
    dropoff_location: gqlBooking.dropoffLocation,
    pickup_date: gqlBooking.pickupDate,
    pickup_time: gqlBooking.pickupTime,
    pickup_type: gqlBooking.pickupType.toLowerCase() as 'asap' | 'scheduled',
    passenger_name: gqlBooking.passengerName,
    company_name: gqlBooking.companyName,
    country_code: gqlBooking.countryCode,
    passenger_mobile: gqlBooking.passengerMobile,
    passenger_email: gqlBooking.passengerEmail,
    payment_method: gqlBooking.paymentMethod,
    communication_prefs: gqlBooking.communicationPrefs || {
      voice: false,
      sms: true,
      whatsapp: false,
      email: true,
    },
    passengers: gqlBooking.passengers,
    luggage: gqlBooking.luggage,
    selected_ride: gqlBooking.selectedRide || '',
    estimated_duration: gqlBooking.estimatedDuration,
    status: gqlBooking.status.toLowerCase().replace('_', '_') as BookingRecord['status'],
    is_verified: gqlBooking.isVerified,
    user_id: gqlBooking.userId || undefined,
    booking_number: gqlBooking.bookingNumber,
    status_reason: gqlBooking.statusReason,
    serviceprovider_id: gqlBooking.serviceProviderId || undefined,
    service_provider_name: gqlBooking.serviceProvider?.serviceProviderName || undefined,
    booking_details: gqlBooking.bookingDetails,
    is_anonymous_booking: gqlBooking.isAnonymousBooking,
  };
}

/**
 * Convert frontend booking data to GraphQL input
 */
function toCreateBookingInput(
  bookingData: Partial<BookingRecord>,
  serviceKey: string
): CreateBookingInput {
  return {
    pickupLocation: bookingData.pickup_location || '',
    dropoffLocation: bookingData.dropoff_location || '',
    pickupDate: bookingData.pickup_date || '',
    pickupTime: bookingData.pickup_time || '',
    pickupType: (bookingData.pickup_type?.toUpperCase() || 'SCHEDULED') as 'ASAP' | 'SCHEDULED',
    passengerName: bookingData.passenger_name || '',
    companyName: bookingData.company_name,
    countryCode: bookingData.country_code || 'AU',
    passengerMobile: bookingData.passenger_mobile || '',
    passengerEmail: bookingData.passenger_email,
    paymentMethod: bookingData.payment_method || 'cash',
    communicationPrefs: bookingData.communication_prefs,
    passengers: bookingData.passengers || 1,
    luggage: bookingData.luggage || 0,
    selectedRide: bookingData.selected_ride,
    estimatedDuration: bookingData.estimated_duration,
    bookingDetails: bookingData.booking_details,
    serviceKey,
  };
}

/**
 * Map status string to GraphQL enum
 */
function toGraphQLStatus(status: string): BookingStatus {
  const statusMap: Record<string, BookingStatus> = {
    pending: 'PENDING',
    pending_confirmation: 'PENDING_CONFIRMATION',
    confirmed: 'CONFIRMED',
    cancelled: 'CANCELLED',
    completed: 'COMPLETED',
  };
  return statusMap[status.toLowerCase()] || 'PENDING';
}

// ============================================
// Booking Service Functions
// ============================================

/**
 * Create a new booking
 * Returns data in array format for frontend compatibility
 */
export async function createBooking(
  bookingData: Partial<BookingRecord>,
  serviceKey: string
): Promise<{
  error: Error | null;
  data: BookingRecord[] | null;
  isAnonymousBooking?: boolean;
  generatedPassword?: string;
  accountCreated?: boolean;
  userEmail?: string;
}> {
  // INFO: Booking creation is a core business event
  logger.info('Creating booking', { serviceKey, pickupType: bookingData.pickup_type, passengerName: bookingData.passenger_name });

  const input = toCreateBookingInput(bookingData, serviceKey);

  const response = await graphqlClient.mutate<CreateBookingResponse>(
    CREATE_BOOKING_MUTATION,
    { input }
  );

  const { data, error } = extractData(response);

  if (error || !data?.createBooking?.booking) {
    // WARN: Booking creation failed
    logger.warn('Booking creation failed', { serviceKey, error });
    return {
      error: new Error(error || 'Failed to create booking'),
      data: null,
    };
  }

  const booking = toBookingRecord(data.createBooking.booking);
  const payload = data.createBooking;

  // INFO: Booking created successfully
  logger.info('Booking created', {
    bookingId: booking.id,
    bookingNumber: booking.booking_number,
    isAnonymous: booking.is_anonymous_booking,
    serviceKey,
  });

  return {
    error: null,
    data: [booking], // Return as array for frontend compatibility
    isAnonymousBooking: booking.is_anonymous_booking,
    generatedPassword: payload.generatedPassword || undefined,
    accountCreated: payload.accountCreated || false,
    userEmail: payload.userEmail || undefined,
  };
}

/**
 * Get all bookings (admin only)
 */
export async function getAllBookings(): Promise<{
  error: Error | null;
  data: BookingRecord[];
}> {
  const response = await graphqlClient.authenticatedQuery<AllBookingsResponse>(
    ALL_BOOKINGS_QUERY
  );

  const { data, error } = extractData(response);

  if (error) {
    return { error: new Error(error), data: [] };
  }

  // Handle paginated connection response with nodes
  const bookings = data?.bookings?.nodes || data?.bookings || [];

  return {
    error: null,
    data: (Array.isArray(bookings) ? bookings : []).map(toBookingRecord),
  };
}

/**
 * Get booking by ID
 */
export async function getBookingById(
  id: string
): Promise<{ error: Error | null; data: BookingRecord | null }> {
  const response = await graphqlClient.query<BookingByIdResponse>(
    BOOKING_BY_ID_QUERY,
    { id }
  );

  const { data, error } = extractData(response);

  if (error) {
    return { error: new Error(error), data: null };
  }

  if (!data?.bookingById) {
    return { error: null, data: null };
  }

  return {
    error: null,
    data: toBookingRecord(data.bookingById),
  };
}

/**
 * Get bookings for current user
 */
export async function getMyBookings(): Promise<{
  error: Error | null;
  data: BookingRecord[];
}> {
  const response = await graphqlClient.authenticatedQuery<MyBookingsResponse>(
    MY_BOOKINGS_QUERY
  );

  const { data, error } = extractData(response);

  if (error) {
    return { error: new Error(error), data: [] };
  }

  return {
    error: null,
    data: (data?.myBookings || []).map(toBookingRecord),
  };
}

/**
 * Get bookings by phone number
 * Note: This would need server-side implementation
 */
export async function getBookingsByPhoneNumber(
  _phone: string,
  _countryCode: string
): Promise<{ error: Error | null; data: BookingRecord[] }> {
  // For now, return user's bookings - server would need a specific endpoint
  return getMyBookings();
}

/**
 * Get bookings by service provider ID
 */
export async function getBookingsByServiceProviderId(
  serviceProviderId: string
): Promise<{ error: Error | null; data: BookingRecord[] }> {
  const response = await graphqlClient.authenticatedQuery<BookingsByServiceProviderResponse>(
    BOOKINGS_BY_SERVICE_PROVIDER_QUERY,
    { serviceProviderId }
  );

  const { data, error } = extractData(response);

  if (error) {
    return { error: new Error(error), data: [] };
  }

  return {
    error: null,
    data: (data?.bookingsByServiceProvider || []).map(toBookingRecord),
  };
}

/**
 * Update booking status
 */
export async function updateBookingStatus(
  id: string,
  status: string,
  statusReason?: string
): Promise<{ error: Error | null; data: BookingRecord | null }> {
  // INFO: Status change is a key lifecycle event
  logger.info('Updating booking status', { bookingId: id, newStatus: status });
  const response = await graphqlClient.authenticatedMutate<UpdateBookingStatusResponse>(
    UPDATE_BOOKING_STATUS_MUTATION,
    {
      input: {
        bookingId: id,
        status: toGraphQLStatus(status),
        statusReason: statusReason,
      },
    }
  );

  const { data, error } = extractData(response);

  if (error || !data?.updateBookingStatus?.booking) {
    return {
      error: new Error(error || 'Failed to update booking status'),
      data: null,
    };
  }

  return {
    error: null,
    data: toBookingRecord(data.updateBookingStatus.booking),
  };
}

/**
 * Confirm an anonymous booking (no authentication required)
 * This is used when an anonymous user confirms their booking
 */
export async function confirmAnonymousBooking(
  id: string
): Promise<{ error: Error | null; data: BookingRecord | null }> {
  const response = await graphqlClient.mutate<ConfirmAnonymousBookingResponse>(
    CONFIRM_ANONYMOUS_BOOKING_MUTATION,
    { bookingId: id }
  );

  const { data, error } = extractData(response);

  if (error || !data?.confirmAnonymousBooking?.booking) {
    return {
      error: new Error(error || 'Failed to confirm booking'),
      data: null,
    };
  }

  return {
    error: null,
    data: toBookingRecord(data.confirmAnonymousBooking.booking),
  };
}

/**
 * Confirm a booking for authenticated users
 * This is used when a logged-in user confirms their own booking
 */
export async function confirmMyBooking(
  id: string
): Promise<{ error: Error | null; data: BookingRecord | null }> {
  const response = await graphqlClient.authenticatedMutate<ConfirmMyBookingResponse>(
    CONFIRM_MY_BOOKING_MUTATION,
    { bookingId: id }
  );

  const { data, error } = extractData(response);

  if (error || !data?.confirmMyBooking?.booking) {
    return {
      error: new Error(error || 'Failed to confirm booking'),
      data: null,
    };
  }

  return {
    error: null,
    data: toBookingRecord(data.confirmMyBooking.booking),
  };
}

/**
 * Cancel a booking (admin only)
 */
export async function cancelBooking(
  id: string,
  reason: string
): Promise<{ error: Error | null; data: BookingRecord | null }> {
  // INFO: Admin cancellation - audit event
  logger.info('Admin cancelling booking', { bookingId: id, reason });
  const response = await graphqlClient.authenticatedMutate<CancelBookingResponse>(
    CANCEL_BOOKING_MUTATION,
    {
      bookingId: id,
      reason,
    }
  );

  const { data, error } = extractData(response);

  if (error || !data?.cancelBooking?.booking) {
    return {
      error: new Error(error || 'Failed to cancel booking'),
      data: null,
    };
  }

  return {
    error: null,
    data: toBookingRecord(data.cancelBooking.booking),
  };
}

/**
 * Cancel a booking for authenticated users
 * This is used when a logged-in user cancels their own booking
 */
export async function cancelMyBooking(
  id: string,
  reason: string
): Promise<{ error: Error | null; data: BookingRecord | null }> {
  // INFO: User self-cancellation - audit event
  logger.info('User cancelling own booking', { bookingId: id, reason });
  const response = await graphqlClient.authenticatedMutate<CancelMyBookingResponse>(
    CANCEL_MY_BOOKING_MUTATION,
    {
      bookingId: id,
      reason,
    }
  );

  const { data, error } = extractData(response);

  if (error || !data?.cancelMyBooking?.booking) {
    return {
      error: new Error(error || 'Failed to cancel booking'),
      data: null,
    };
  }

  return {
    error: null,
    data: toBookingRecord(data.cancelMyBooking.booking),
  };
}

/**
 * Update booking (generic update)
 */
export async function updateBooking(
  id: string,
  bookingData: Partial<BookingRecord>
): Promise<{ error: Error | null; data: BookingRecord | null }> {
  // Handle status updates
  if (bookingData.status) {
    return updateBookingStatus(id, bookingData.status, bookingData.status_reason);
  }
  // For non-status updates, return current booking
  // Full booking updates would need server-side implementation
  return getBookingById(id);
}

/**
 * Get user's address history from bookings
 */
export async function getUserAddressHistory(): Promise<{
  error: Error | null;
  data: Array<{ id: string; pickup: string; destination: string }>;
}> {
  const { data, error } = await getMyBookings();

  if (error) {
    return { error, data: [] };
  }

  if (!data || data.length === 0) {
    return { error: null, data: [] };
  }

  // Get unique pickup/destination combinations
  const addressMap = new Map<string, { id: string; pickup: string; destination: string }>();

  data.forEach((booking, index) => {
    const key = `${booking.pickup_location}|${booking.dropoff_location}`;
    if (!addressMap.has(key)) {
      addressMap.set(key, {
        id: `history-${index}`,
        pickup: booking.pickup_location,
        destination: booking.dropoff_location,
      });
    }
  });

  return { error: null, data: Array.from(addressMap.values()) };
}

// ============================================
// Verification Service (Simplified)
// ============================================

export const verificationService = {
  /**
   * All bookings are considered verified in the GraphQL backend
   */
  isVerified: () => true,

  /**
   * Verify booking (no-op, always returns success)
   */
  verifyBooking: async (_bookingId: string, _code: string) => ({
    success: true,
    error: null,
  }),
};

// ============================================
// Contact Service
// ============================================

export const contactService = {
  /**
   * Submit contact form (positional args)
   */
  submitContact: async (
    name: string,
    email: string,
    message: string,
    serviceKey?: string
  ): Promise<{ success: boolean; error: string | null }> => {
    const input: ContactInput = { name, email, message, serviceKey };

    const response = await graphqlClient.mutate<SubmitContactResponse>(
      SUBMIT_CONTACT_MUTATION,
      { input }
    );

    const { error } = extractData(response);

    return {
      success: !error,
      error,
    };
  },

  /**
   * Submit contact form (object args - used by About.tsx)
   * Note: user_id is handled automatically via JWT token on backend
   */
  submitContactForm: async (data: {
    name: string;
    email: string;
    message: string;
    user_id?: string | null;
    service_key?: string | null;
  }): Promise<{ success: boolean; error: string | null }> => {
    const input: ContactInput = {
      name: data.name,
      email: data.email,
      message: data.message,
      serviceKey: data.service_key || undefined,
    };

    const response = await graphqlClient.mutate<SubmitContactResponse>(
      SUBMIT_CONTACT_MUTATION,
      { input }
    );

    const { error } = extractData(response);

    return {
      success: !error,
      error,
    };
  },

  /**
   * Submit feedback (object args - used by About.tsx)
   * Note: user_id is handled automatically via JWT token on backend
   */
  submitFeedback: async (data: {
    rating: number;
    feedback: string;
    user_id?: string | null;
    service_key?: string | null;
  }): Promise<{ success: boolean; error: string | null }> => {
    const input: FeedbackInput = {
      rating: data.rating,
      feedbackText: data.feedback,
      serviceKey: data.service_key || undefined,
    };

    const response = await graphqlClient.mutate<SubmitFeedbackResponse>(
      SUBMIT_FEEDBACK_MUTATION,
      { input }
    );

    const { error } = extractData(response);

    return {
      success: !error,
      error,
    };
  },
};

// ============================================
// Feedback Service
// ============================================

export interface FeedbackFormData {
  rating: number;
  feedback: string;
  serviceKey?: string;
}

export const feedbackService = {
  /**
   * Submit feedback
   */
  submitFeedback: async (
    feedbackData: FeedbackFormData
  ): Promise<{ success: boolean; error: string | null }> => {
    const input: FeedbackInput = {
      rating: feedbackData.rating,
      feedbackText: feedbackData.feedback,
      serviceKey: feedbackData.serviceKey,
    };

    const response = await graphqlClient.mutate<SubmitFeedbackResponse>(
      SUBMIT_FEEDBACK_MUTATION,
      { input }
    );

    const { error } = extractData(response);

    return {
      success: !error,
      error,
    };
  },
};

// ============================================
// Named Export for Compatibility
// ============================================

export const bookingService = {
  createBooking,
  getAllBookings,
  getBookingById,
  getMyBookings,
  getBookingsByPhoneNumber,
  getBookingsByServiceProviderId,
  updateBookingStatus,
  confirmAnonymousBooking,
  confirmMyBooking,
  cancelBooking,
  cancelMyBooking,
  updateBooking,
  getUserAddressHistory,
  verificationService,
  contactService,
  feedbackService,
};

// ============================================
// Default Export
// ============================================

export default bookingService;
